import { useMemo } from 'react';
import { useCardGet } from './use-card-resource';
import styles from './expand.module.scss';
import { ExpandAccordion, ExpandState, ExpandTable } from './common';
import { formatWholeDollarCurrency } from 'utils/number';

// AFE cost figures are large, whole-dollar amounts — match the mock (no cents).
const money = formatWholeDollarCurrency;

// AFE cost overview (GET /documents/:id/afe_structuring/costs). Keys are
// camelCase on the wire. Each live well is one AfeHeader with its category
// rollups; the Cost Summary lists them per well (mock parity), not aggregated.
interface AfeWellRow {
  afeHeaderId: number;
  wellName: string | null;
  operator: string | null;
  basin: string | null;
  countyState: string | null;
  removed: boolean;
  workingInterestPercentage: number | null;
  drilling: number | null;
  completion: number | null;
  facilities: number | null;
  contingencies: number | null;
  other: number | null;
  grandTotal: number | null;
}

interface AfeCostResponse {
  summary: { grandTotal: number | null };
  wells: AfeWellRow[];
}

interface CostRow {
  label: string;
  amount: number;
}

const COST_CATEGORIES: { key: keyof AfeWellRow; label: string }[] = [
  { key: 'drilling', label: 'Drilling' },
  { key: 'completion', label: 'Completion' },
  { key: 'facilities', label: 'Facilities' },
  { key: 'contingencies', label: 'Contingencies' },
  { key: 'other', label: 'Other' },
];

// Non-zero category rows for one well, from its header's cost rollups.
const wellCostRows = (well: AfeWellRow): CostRow[] =>
  COST_CATEGORIES.map(({ key, label }) => ({
    label,
    amount: (well[key] as number | null) ?? 0,
  })).filter((row) => row.amount !== 0);

export const AfeExpand = ({
  documentId,
  openSignal,
}: {
  documentId: number | string;
  // Broadcast from the card's Expand-All / Collapse-All control.
  openSignal?: { open: boolean };
}) => {
  const url = `/documents/${documentId}/afe_structuring/costs`;
  const { loading, data, error } = useCardGet<AfeCostResponse>(url);

  const liveWells = useMemo(
    () => (data?.wells ?? []).filter((w) => !w.removed),
    [data]
  );

  if (loading) return <ExpandState>Loading…</ExpandState>;
  if (error) return <ExpandState>Failed to load AFE details.</ExpandState>;
  if (liveWells.length === 0)
    return <ExpandState>No AFE details available.</ExpandState>;

  return (
    <ExpandAccordion
      openSignal={openSignal}
      title={`Cost Summary (${liveWells.length} ${
        liveWells.length === 1 ? 'well' : 'wells'
      })`}
    >
      <div className={styles.wellItems}>
        {liveWells.map((well) => {
          const rows = wellCostRows(well);
          const wellTotal =
            well.grandTotal ?? rows.reduce((sum, row) => sum + row.amount, 0);
          return (
            <ExpandAccordion
              key={well.afeHeaderId}
              // Wells collapsed by default; open on click (mock parity).
              title={
                <>
                  {well.wellName ?? 'Well'}{' '}
                  <span className={styles.wellTotal}>{money(wellTotal)}</span>
                </>
              }
            >
              <ExpandTable<CostRow>
                bordered
                columns={[
                  { header: 'Cost Category', cell: (row) => row.label },
                  { header: 'Amount', cell: (row) => money(row.amount) },
                ]}
                rows={rows}
                rowKey={(row) => row.label}
                footer={['Well Total', money(wellTotal)]}
              />
            </ExpandAccordion>
          );
        })}
      </div>
    </ExpandAccordion>
  );
};
